import { Button, type ToastType } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useOutletContext, Link } from "react-router-dom";
import { crmWebhookLogs, type CrmWebhookLog } from "../../api/crm-webhook-logs.service.js";
import { crmPackageMappings } from "../../api/crm-package-mappings.service.js";
import { UnauthorizedView } from "../../components/UnauthorizedView/index.js";
import EmptyState from "../../components/EmptyState/index.js";
import LoadingState from "../../components/LoadingState/index.js";
import { WebhookLogTimeline } from "../../components/WebhookLogTimeline/index.js";
import { formatDateShort } from "../../utils/dateFormatter.js";
import styles from "./CrmWebhookLogDetail.module.css";

type CrmWebhookLogDetailContext = {
  setToast?: (toast: { message: string; type: ToastType } | null) => void;
};

function CrmWebhookLogDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const context = useOutletContext<CrmWebhookLogDetailContext>();
  const [log, setLog] = useState<CrmWebhookLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [markingHandled, setMarkingHandled] = useState(false);
  const [showRawPayload, setShowRawPayload] = useState(true);
  const [showResponseBody, setShowResponseBody] = useState(true);
  const [showAutoResolved, setShowAutoResolved] = useState(true);

  useEffect(() => {
    if (id) {
      loadLog(id);
    }
  }, [id]);

  async function loadLog(logId: string) {
    try {
      setLoading(true);
      setError(null);
      const logData = await crmWebhookLogs.getById(logId);
      setLog(logData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load log"));
    } finally {
      setLoading(false);
    }
  }

  async function handleRetry() {
    if (!id) return;
    setRetrying(true);
    try {
      const result = await crmWebhookLogs.retry(id);
      context.setToast?.({
        message:
          result.status === "created"
            ? "Retry successful — subscription created"
            : `Retry completed with status: ${result.status}`,
        type: result.status === "created" ? "success" : "warning",
      });
      if (result.logId) {
        navigate(`/fulfilment/crm-webhook-logs/${result.logId}`);
      }
    } catch (err) {
      context.setToast?.({
        message: `Retry failed: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setRetrying(false);
    }
  }

  async function handleMarkHandled() {
    if (!log) return;
    setMarkingHandled(true);
    try {
      await crmWebhookLogs.markHandledBulk([log.id]);
      setLog({ ...log, handled: true });
      context.setToast?.({
        message: "Log resolved",
        type: "success",
      });
    } catch (err) {
      context.setToast?.({
        message: `Failed to resolve: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setMarkingHandled(false);
    }
  }

  async function handleSkipProduct() {
    if (!log) return;
    const rawPayload = log.rawPayload as Record<string, unknown>;
    const productId = (rawPayload.productId || rawPayload.productCode) as string | undefined;
    if (!productId) return;

    setSkipping(true);
    try {
      await crmPackageMappings.createSkip(productId);
      context.setToast?.({
        message: `Product ${productId} marked as skip`,
        type: "success",
      });
      loadLog(log.id);
    } catch (err) {
      context.setToast?.({
        message: `Failed to skip product: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setSkipping(false);
    }
  }

  function getStatusBadgeClass(status: string) {
    switch (status) {
      case "created":
        return styles.statusCreated;
      case "duplicate":
        return styles.statusDuplicate;
      case "failed":
        return styles.statusFailed;
      case "dropped":
        return styles.statusDropped;
      default:
        return "";
    }
  }

  if (loading) {
    return <LoadingState message="Loading webhook log..." />;
  }

  if (error || !log) {
    const status = error && (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;

    if (isAuthError) {
      return <UnauthorizedView />;
    }

    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Failed to load webhook log"
          message={error?.message || "Log not found"}
          action={{
            label: "Back to Logs",
            onClick: () => navigate("/fulfilment/crm-webhook-logs"),
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h1 className={styles.logTitle}>{log.correlationId || log.id}</h1>
        </div>
        <div className={styles.headerActions}>
          {log.errorCode === "package_mapping_missing" && (
            <>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const productId = (log.rawPayload as Record<string, unknown>).productId;
                  navigate(
                    `/fulfilment/crm-package-mappings?create=true&crmPackageId=${encodeURIComponent(String(productId || ""))}`,
                  );
                }}
              >
                Add Package Mapping
              </Button>
              <Button
                variant="outlined"
                color="default"
                onClick={handleSkipProduct}
                disabled={skipping}
              >
                {skipping ? "Skipping..." : "Skip This Product"}
              </Button>
            </>
          )}
          {log.errorCode === "firm_not_found" && !!log.crmFirmId && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                navigate(`/address-book/companies/new?stash=${encodeURIComponent(log.id)}`);
              }}
            >
              Create firm in address book from this CRM account
            </Button>
          )}
          {log.status === "failed" && !log.handled && (
            <Button variant="contained" color="primary" onClick={handleRetry} disabled={retrying}>
              {retrying ? "Retrying..." : "Retry"}
            </Button>
          )}
          {!log.handled && (
            <Button
              variant="outlined"
              color="default"
              onClick={handleMarkHandled}
              disabled={markingHandled}
            >
              {markingHandled ? "Resolving..." : "Resolve"}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Request Information</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Log ID</span>
              <span className={styles.infoValueMono}>{log.id}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Correlation ID</span>
              <span className={styles.infoValueMono}>
                {log.correlationId || <span className={styles.emptyText}>Not provided</span>}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>CRM Firm ID</span>
              <span className={styles.infoValueMono}>
                {log.crmFirmId || <span className={styles.emptyText}>Not provided</span>}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Region Name</span>
              <span className={styles.infoValue}>
                {log.regionName || <span className={styles.emptyText}>Not provided</span>}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Received At</span>
              <span className={styles.infoValue}>{formatDateShort(log.createdAt)}</span>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Processing Result</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Status</span>
              <span className={`${styles.statusBadge} ${getStatusBadgeClass(log.status)}`}>
                {log.status}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>HTTP Response Code</span>
              <span className={styles.infoValue}>{log.responseStatusCode}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Subscription ID</span>
              {log.subscriptionId ? (
                <Link
                  to={`/fulfilment/subscriptions/${log.subscriptionId}`}
                  className={styles.subscriptionLink}
                >
                  <span className={styles.infoValueMono}>{log.subscriptionId}</span>
                </Link>
              ) : (
                <span className={styles.emptyText}>Not created</span>
              )}
            </div>
          </div>

          {log.errorMessage && (
            <div style={{ marginTop: "var(--spacing-4)" }}>
              <span className={styles.infoLabel}>Error Message</span>
              <div className={styles.errorMessage}>{log.errorMessage}</div>
            </div>
          )}
        </div>

        <WebhookLogTimeline key={log.id} correlationId={log.correlationId} currentLogId={log.id} />

        {log.autoResolvedData && Object.keys(log.autoResolvedData).length > 0 && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Auto-Resolved Data</h2>
            <p style={{ color: "var(--color-text-secondary)", marginBottom: "var(--spacing-3)" }}>
              Data automatically resolved during processing
            </p>
            <div className={styles.jsonSection}>
              <button
                className={styles.jsonToggle}
                onClick={() => setShowAutoResolved(!showAutoResolved)}
              >
                <span>{showAutoResolved ? "▼" : "▶"}</span>
                <span>{showAutoResolved ? "Hide" : "Show"} resolved data</span>
              </button>
              {showAutoResolved && (
                <div className={styles.jsonContent}>
                  <pre className={styles.jsonPre}>
                    {JSON.stringify(log.autoResolvedData, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Raw Request Payload</h2>
          <p style={{ color: "var(--color-text-secondary)", marginBottom: "var(--spacing-3)" }}>
            The original request body received from CRM
          </p>
          <div className={styles.jsonSection}>
            <button
              className={styles.jsonToggle}
              onClick={() => setShowRawPayload(!showRawPayload)}
            >
              <span>{showRawPayload ? "▼" : "▶"}</span>
              <span>{showRawPayload ? "Hide" : "Show"} raw payload</span>
            </button>
            {showRawPayload && (
              <div className={styles.jsonContent}>
                <pre className={styles.jsonPre}>{JSON.stringify(log.rawPayload, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>

        {log.responseBody && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Response Body</h2>
            <p style={{ color: "var(--color-text-secondary)", marginBottom: "var(--spacing-3)" }}>
              The response returned to CRM
            </p>
            <div className={styles.jsonSection}>
              <button
                className={styles.jsonToggle}
                onClick={() => setShowResponseBody(!showResponseBody)}
              >
                <span>{showResponseBody ? "▼" : "▶"}</span>
                <span>{showResponseBody ? "Hide" : "Show"} response body</span>
              </button>
              {showResponseBody && (
                <div className={styles.jsonContent}>
                  <pre className={styles.jsonPre}>{JSON.stringify(log.responseBody, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default CrmWebhookLogDetail;
